import { useEffect, useReducer, useRef } from 'react';
import PackSettingsService from '../../api/packSettingsService';
import { PackSettings } from '../../types/packSettings';
import toast from 'react-hot-toast';
import { LevelDefaults } from '../../types/level';

type Action =
  | { type: 'fetch' }
  | { type: 'loaded'; value: PackSettings[] | null }
  | { type: 'add' }
  | { type: 'update'; index: number; pack: PackSettings }
  | { type: 'merge-up'; index: number }
  | { type: 'merge-down'; index: number };

interface PackSettingsState {
  Records: PackSettings[];
  Loading: boolean;
}

const DefaultPackSettingsState = {
  Records: [],
  Loading: false,
} as PackSettingsState;

const reducer = (state: PackSettingsState, action: Action): PackSettingsState => {
  switch (action.type) {
    case 'fetch':
      return { ...state, Loading: true };
    case 'loaded': {
      if (action.value === null) {
        return { ...state, Loading: false };
      }

      return { ...state, Records: [...action.value], Loading: false };
    }
    case 'add': {
      const last =
        state.Records.length > 0
          ? state.Records[state.Records.length - 1]
          : ({
              end: 0,
              levelWidth: 12,
              levelHeight: 9,
              minSize: {
                min: LevelDefaults.MinWordSize,
                max: LevelDefaults.MaxWordSize,
              },
              wordsCount: {
                min: LevelDefaults.MinWordsCount,
                max: LevelDefaults.MaxWordsCount,
              },
              keywordLength: {
                min: LevelDefaults.MinKeywordLength,
                max: LevelDefaults.MaxKeywordLength,
              },
            } as PackSettings);
      const record = {
        ...last,
        start: last.end + 1,
        end: last.end + 2,
      } as PackSettings;
      return { ...state, Records: [...state.Records.concat([record])] };
    }
    case 'update': {
      let offset = 0;
      return {
        ...state,
        Records: [
          ...state.Records.map((r, ind) => {
            if (ind === action.index) {
              offset = action.pack.end;
              return { ...action.pack };
            } else if (ind > action.index) {
              const size = r.end - r.start;
              const rcd = { ...r, start: offset + 1, end: offset + 1 + size };
              offset = rcd.end;
              return rcd;
            }

            return { ...r };
          }),
        ],
      };
    }
    case 'merge-up': {
      if (action.index === 0) return state;
      const current = state.Records[action.index];
      return {
        ...state,
        Records: [
          ...state.Records.map((val, ind) => {
            if (ind !== action.index - 1) return { ...val };
            return { ...val, end: current.end };
          }).filter((_, ind) => ind !== action.index),
        ],
      };
    }
    case 'merge-down': {
      if (action.index === state.Records.length - 1) return state;
      const current = state.Records[action.index];
      return {
        ...state,
        Records: [
          ...state.Records.map((val, ind) => {
            if (ind !== action.index + 1) return { ...val };
            return { ...current, end: val.end };
          }).filter((_, ind) => ind !== action.index),
        ],
      };
    }
    default:
      return state;
  }
};

export const usePackSettingsReducer = () => {
  const [data, dispatch] = useReducer(reducer, DefaultPackSettingsState);
  const loading = useRef<boolean>(false);

  useEffect(() => {
    if (loading.current) return;
    loading.current = true;
    fetchData();
  }, []);

  const fetchData = async () => {
    dispatch({ type: 'fetch' });
    const records = await PackSettingsService.getAll();
    dispatch({ type: 'loaded', value: records });
  };

  const addRecord = () => dispatch({ type: 'add' });
  const mergeUp = (index: number) => dispatch({ type: 'merge-up', index: index });
  const mergeDown = (index: number) => dispatch({ type: 'merge-down', index: index });
  const update = (index: number, pack: PackSettings) =>
    dispatch({ type: 'update', index: index, pack: pack });

  const sync = async (): Promise<void> => {
    dispatch({ type: 'fetch' });
    const saved = await PackSettingsService.sync(data.Records);
    const records = await PackSettingsService.getAll();
    dispatch({ type: 'loaded', value: records });

    if (saved) {
      toast.success('settings saved');
    } else {
      toast.error('failed');
    }
  };

  return {
    data,
    addRecord,
    mergeUp,
    mergeDown,
    update,
    sync,
  };
};
